import { BASE_LABELS, NAV_LABEL_KEYS } from '../constants/labels';
import { hasTabFilter } from './navModel';

/**
 * Module-level label store so plain data modules (e.g. the *Columns arrays in utils.js)
 * can resolve labels without hooks. The LabelsProvider keeps it in sync with the server;
 * localStorage seeds it before the first fetch so labels paint correctly on load.
 * Column definitions should expose labels as getters (get label() { return getLabel(k); })
 * so they resolve at render time, not at module-import time.
 */
const STORAGE_KEY = 'terminologyOverrides';
const DEFAULTS_KEY = 'terminologyPresetDefaults';

let overrides = {};
// Business-preset wording served with the overrides (GET /config/terminology
// `defaults`): between BASE_LABELS and the deployment's own overrides.
let presetDefaults = {};
try {
	const cachedDefaults = localStorage.getItem(DEFAULTS_KEY);
	if (cachedDefaults) presetDefaults = JSON.parse(cachedDefaults) || {};
} catch {
	presetDefaults = {};
}
try {
	const cached = localStorage.getItem(STORAGE_KEY);
	if (cached) overrides = JSON.parse(cached) || {};
} catch {
	overrides = {};
}

const listeners = new Set();

export const getLabel = (key) => {
	const value = overrides[key] !== undefined && overrides[key] !== ''
		? overrides[key]
		: (presetDefaults[key] !== undefined ? presetDefaults[key] : BASE_LABELS[key]);
	return value !== undefined ? value : key;
};

export const getOverrides = () => ({ ...overrides });

/**
 * Nav tab labels come from the ObjectTab collection (DB). Only an explicit
 * terminology override beats the stored tab label; the base dictionary does not,
 * so deployments with custom ObjectTab labels keep them.
 */
export const getNavLabel = (schema, fallback) => {
	const key = NAV_LABEL_KEYS[schema];
	const override = key ? overrides[key] : undefined;
	return override !== undefined && override !== '' ? override : fallback;
};

/**
 * Label of an ObjectTab row. A filtered or routed tab (e.g. 'Clientes' over
 * accounts, 'Liquidaciones de socios' over credits) is not the entity's list,
 * so the entity rename must not replace its own label.
 */
export const getTabLabel = (tab) => {
	if (!tab) return '';
	if (tab.path || hasTabFilter(tab)) return tab.label;
	return getNavLabel(tab.schema, tab.label);
};

/** Menu group label: 'nav.group.<key>', else the key as typed in Pestañas. */
export const getNavGroupLabel = (groupKey) => {
	const labelKey = `nav.group.${groupKey}`;
	const label = getLabel(labelKey);
	return label === labelKey ? groupKey : label;
};

/** Base value for a key under the preset wording (what an empty override shows). */
export const getDefaultLabel = (key) => (presetDefaults[key] !== undefined ? presetDefaults[key] : BASE_LABELS[key]);

export const setOverrides = (next, defaults) => {
	overrides = next && typeof next === 'object' ? next : {};
	if (defaults !== undefined) {
		presetDefaults = defaults && typeof defaults === 'object' ? defaults : {};
		try {
			localStorage.setItem(DEFAULTS_KEY, JSON.stringify(presetDefaults));
		} catch {
			// storage unavailable: preset wording still applies this session
		}
	}
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
	} catch {
		// storage unavailable (private mode/quota): labels still work for this session
	}
	listeners.forEach((listener) => listener());
};

export const subscribeToLabels = (listener) => {
	listeners.add(listener);
	return () => listeners.delete(listener);
};
